import { Box, Paper, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '@shared/components/spinner/Spinner';
import { IconHeadphones } from '@shared/assets/images/svgs';
import { TabSectionHeader } from './TabSectionHeader';
import {
  AgentDto,
  CampaignDto,
  HandoffRuleRequestDto,
  apiService,
  useHandoffRuleCreateControllerExecuteMutation,
} from '@shared/services/apiService/apiService';
import { useAppDispatch } from '@shared/store';
import { HandoffRulesEditor } from '@pages/Admin/Main/Agent/ui/components/HandoffRulesEditor';
import { SQUAD_CONFIG, SQUAD_MESSAGES } from '@features/Admin/Agent/constants/squad.constants';
import {
  parseHandoffRulesValue,
  serializeHandoffRules,
  serializeHandoffRulesDocument,
} from '@features/Admin/Agent/utils/handoff-rule-mapper';
import { useBeforeUnload } from '@shared/hooks/useBeforeUnload';
import { RouterBlockerDialog } from '@shared/hooks/useRouterBlockerDialog';

const handoffRulesPayloadMatches = (a: string, b: string): boolean => {
  const parsedA = parseHandoffRulesValue(a);
  const parsedB = parseHandoffRulesValue(b);
  if (parsedA.hasError || parsedB.hasError) return false;
  return (
    JSON.stringify(serializeHandoffRules(parsedA.rules)) ===
    JSON.stringify(serializeHandoffRules(parsedB.rules))
  );
};

const mergeApiHandoffRulesIntoDocument = (currentValue: string, apiRulesJson: string): string => {
  const current = parseHandoffRulesValue(currentValue);
  const api = parseHandoffRulesValue(apiRulesJson);
  if (api.hasError) return currentValue;
  if (current.hasError) return apiRulesJson;

  return JSON.stringify(
    serializeHandoffRulesDocument(api.rules, current.canvasAgentIds, current.nodePositions),
    null,
    2,
  );
};

interface SquadsTabProps {
  agents: AgentDto[];
  isLoading: boolean;
  campaign?: CampaignDto;
  refetch?: () => Promise<unknown>;
}

const SquadsTab: FC<SquadsTabProps> = ({ agents, isLoading, campaign, refetch }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isDisabled = false;
  const squad = campaign?.squad;
  const [saveHandoffRules, { isLoading: isSaving }] =
    useHandoffRuleCreateControllerExecuteMutation();
  const [isDirty, setIsDirty] = useState(false);
  const optimisticSavedRulesRef = useRef<string | null>(null);

  const initialHandoffRules = useMemo(() => {
    if (squad?.handoffRules !== undefined) {
      return JSON.stringify(squad.handoffRules, null, 2);
    }
    return SQUAD_CONFIG.DEFAULT_HANDOFF_RULES;
  }, [squad?.handoffRules]);

  const [handoffRules, setHandoffRules] = useState(initialHandoffRules);
  const handoffRulesRef = useRef(handoffRules);
  const lastSyncedInitialRef = useRef<string>(initialHandoffRules);

  useEffect(() => {
    handoffRulesRef.current = handoffRules;
  }, [handoffRules]);

  useEffect(() => {
    // Keep local edits stable while the user is working; campaign refreshes can
    // otherwise overwrite keystrokes in the structured editor.
    const userHasLocalEdits =
      handoffRules !== lastSyncedInitialRef.current && !optimisticSavedRulesRef.current;
    if (isDirty || userHasLocalEdits) return;
    if (optimisticSavedRulesRef.current) {
      if (handoffRulesPayloadMatches(optimisticSavedRulesRef.current, initialHandoffRules)) {
        optimisticSavedRulesRef.current = null;
        const mergedValue = mergeApiHandoffRulesIntoDocument(
          handoffRulesRef.current,
          initialHandoffRules,
        );
        setHandoffRules(mergedValue);
        lastSyncedInitialRef.current = mergedValue;
        return;
      }
      setHandoffRules(optimisticSavedRulesRef.current);
      lastSyncedInitialRef.current = optimisticSavedRulesRef.current;
      return;
    }
    setHandoffRules(initialHandoffRules);
    lastSyncedInitialRef.current = initialHandoffRules;
    setIsDirty(false);
  }, [handoffRules, initialHandoffRules, isDirty]);

  useEffect(() => {
    optimisticSavedRulesRef.current = null;
  }, [squad?.id]);

  const handleSave = useCallback(
    async (payload: HandoffRuleRequestDto[]) => {
      if (!squad?.id || isDisabled) {
        return { ok: false, message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVE_FAILED) };
      }
      const response = await saveHandoffRules({ id: squad.id, body: payload });
      if ('error' in response) {
        return { ok: false, message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVE_FAILED) };
      }
      const savedSerializedValue = handoffRulesRef.current;
      const savedDocument = parseHandoffRulesValue(savedSerializedValue);
      optimisticSavedRulesRef.current = savedSerializedValue;
      lastSyncedInitialRef.current = savedSerializedValue;
      setIsDirty(false);

      if (!savedDocument.hasError && campaign?.id) {
        const patchedDocument = serializeHandoffRulesDocument(
          savedDocument.rules,
          savedDocument.canvasAgentIds,
          savedDocument.nodePositions,
        );
        dispatch(
          apiService.util.updateQueryData(
            'campaignControllerCampaign',
            { id: campaign.id },
            (draft) => {
              if (!draft?.squad) return;
              draft.squad.handoffRules = patchedDocument.rules as typeof draft.squad.handoffRules;
            },
          ),
        );
      }

      try {
        await refetch?.();
      } catch {
        // Save succeeded; campaign cache was patched optimistically above.
      }
      return { ok: true, message: t(SQUAD_MESSAGES.HANDOFF_RULES_SAVED) };
    },
    [campaign?.id, dispatch, isDisabled, refetch, saveHandoffRules, squad?.id, t],
  );

  useBeforeUnload(() => isDirty);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box width="100%" sx={{ padding: 4 }}>
      <RouterBlockerDialog
        isBlocked={isDirty}
        titleKey="dialog.title2"
        message={t('campaignPage.squads.editor.unsavedLeaveWarning')}
      />
      {!squad?.id ? (
        <>
          <TabSectionHeader icon={IconHeadphones} title={t('campaignPage.editCampaign.squads')} />
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('adminMainPage.squad.descriptions.singleAgentConfig')}
            </Typography>
          </Paper>
        </>
      ) : (
        <HandoffRulesEditor
          value={handoffRules}
          onChange={setHandoffRules}
          isDisabled={isDisabled}
          agents={agents}
          isSaving={isSaving}
          onSave={handleSave}
          onDirtyChange={setIsDirty}
        />
      )}
    </Box>
  );
};

export default SquadsTab;
